import API, { GraphQLResult, graphqlOperation } from '@aws-amplify/api';
import { Modes, OnguardReaderMaskActionTypes, Regions } from 'src/constants/Constants';
import * as APIt from "../API";
import { listChildDevices, listDeviceStatusForSites } from '../graphql/queries'
import {
  setReaderMode as setReaderModeMutation,
  setInputMask as setInputMaskMutation,
  setReaderDoorHeldMask as setReaderDoorHeldMaskMutation,
  setReaderDoorForcedMask as setReaderDoorForcedMaskMutation,
  setPacsDataApiReaderMask as setPACSDataAPIReaderMaskMutation
 } from '../graphql/mutations'
import { createUserAction } from 'src/utils/UserActionsUtils';
import { UserActionNames } from 'src/constants/Constants';
import { debug } from './commonUtils';

export const fetchDevices = async (sitename: string, siteRegion: string | null): Promise<APIt.ChildDevice[]> => {
  debug(`fetchDevices() sitename is ${sitename} siteRegion is ${siteRegion}`);

  let stage = 'prod';

  let devices: APIt.ChildDevice[] = [];

  if (!sitename) return devices;

  try {
    for (let region of Regions) {
      if (!siteRegion || region === siteRegion) {
        const devicesResponse = await API.graphql(graphqlOperation(listChildDevices,
          {
            path: `/FlexQuery/SilencioV2_SiteFilter_ChildDevices/region/${region}/stage/${stage === 'test' ? 'BETA' : stage.toUpperCase()}/?SiteName=${sitename}&OutputFormat=json` // TODO: pull region from site
          })) as GraphQLResult<APIt.ListChildDevicesQuery>;
        debug(`fetchDevices() devicesResponse is ${JSON.stringify(devicesResponse)}`);
        devices = [...devices, ...devicesResponse.data?.listChildDevices as APIt.ChildDevice[]];
      }
      if (devices.length > 0) break;
    }
    debug(`fetchDevices() devices is ${JSON.stringify(devices)}`);
    const devicesStatus = await fetchDeviceStatusForSite(sitename);
    if (devicesStatus.length > 0) {
      debug(`fetchDevices() devicesStatus is not null, merging status into devices`);
      debug(`fetchDevices() devicesStatus.length is ${devicesStatus.length}`);
      const devicesWithStatus: APIt.ChildDevice[] = devices.map(d => {
        debug(`fetchDevices() devicesStatus.length is ${devicesStatus.length}`);
        debug(`fetchDevices() devices.map d is ${JSON.stringify(d)}`);
        const dStatus = devicesStatus.find(ds =>
          (ds.parent_id == d.Parent_DeviceID
          && ds.child_id == d.Child_DeviceID
          && ds.subchild_id == d.Subchild_DeviceID));
        const mergedDevice = {
          ...d,
          DoorForcedMasked: dStatus?.door_forced_masked === null ? null : dStatus?.door_forced_masked,
          DoorHeldMasked: dStatus?.door_held_masked === null ? null : dStatus?.door_held_masked,
          Masked: dStatus?.masked === null ? null : dStatus?.masked,
          Mode: dStatus?.reader_mode === null ? null : dStatus?.reader_mode
        };
        debug(`fetchDevices() mergedDevice d is ${JSON.stringify(mergedDevice)}`);
        return mergedDevice;
      })
      debug(`fetchDevices() devicesWithStatus.length is ${devicesWithStatus.length}`);
      debug(`fetchDevices() devicesWithStatus is ${JSON.stringify(devicesWithStatus)}`);
      return devicesWithStatus;
    }
    debug(`fetchDevices() devices.length is ${devices.length}`);
    if (stage.toLowerCase() === 'test' || stage.toLowerCase() === 'beta') {
      debug(`fetchDevices() mocking any missing status data for '${stage}' stage`);
      devices = devices.map(d => {
        const rndm = Math.floor(Math.random() * 10);
        return {
          ...d,
          Masked:
            (d.Masked === undefined || d.Masked === null) ? (rndm%2 === 0) ? true : (rndm%3 === 0) ? false : undefined : d.Masked,
          DoorForcedMasked: (d.DoorForcedMasked === undefined || d.DoorForcedMasked === null) ? (rndm%2 === 0) ? true : (rndm%3 === 0) ? false : undefined : d.DoorForcedMasked,
          DoorHeldMasked: (d.DoorHeldMasked === undefined || d.DoorHeldMasked === null) ? (rndm%2 === 0) ? true : (rndm%3 === 0) ? false : undefined : d.DoorHeldMasked,
          Mode: (d.Mode === undefined || d.Mode === null) ? Modes[Math.floor(Math.random() * Modes.length)].value : d.Mode
        }
      });
    }
    return devices;
  } catch(error) {
    console.error(`fetchDevices() error is ${error} JSON.stringify: ${JSON.stringify(error)}`);
    throw error;
  }
}

export const fetchDeviceStatusForSite = async (sitename: string): Promise<APIt.DeviceStatus[]> => {
  debug(`fetchDeviceStatusForSite() sitename is ${sitename}`);

  let devicesStatus: APIt.DeviceStatus[] = [];

  if (!sitename) return devicesStatus;

  try {
    const devicesStatusResponse = await API.graphql(graphqlOperation(listDeviceStatusForSites,
      {
        queryStringParameters: {
          SITE_CODES: `${sitename}`
        }
      })) as GraphQLResult<APIt.ListDeviceStatusForSitesQuery>;
    debug(`fetchDeviceStatusForSite() devicesStatusResponse is ${JSON.stringify(devicesStatusResponse)}`);
    devicesStatus = devicesStatusResponse.data?.listDeviceStatusForSites as APIt.DeviceStatus[];
    debug(`fetchDeviceStatusForSite() devicesStatus is ${JSON.stringify(devicesStatus)}`);
    return devicesStatus;
  } catch(error) {
    console.error(`fetchDeviceStatusForSite() error is ${error} JSON.stringify ${JSON.stringify(error)}`);
    throw error;
  }
}

export interface SetReaderModeResponseInterface {
  devices: APIt.SetDeviceResponse[],
  error: string | null | undefined;
  requestUUID: string | null | undefined;
  status: number | null | undefined;
}

export const setReaderMode = async (
  deviceSource: string,
  devices: { deviceName: string, deviceHref: string | undefined }[],
  emails: string,
  mode: string,
  processWithDelaySeconds: number,
  requestedBy: string,
  siteName: string,
  siteRegion: string): Promise<SetReaderModeResponseInterface> =>
{
  debug(`setReaderMode() deviceSource is ${deviceSource} devices is ${JSON.stringify(devices)} emails is ${emails} mode is ${JSON.stringify(mode)} processWithDelaySeconds is ${processWithDelaySeconds} requestedBy is ${requestedBy} siteName is ${siteName} siteRegion is ${siteRegion}`);

  let setReaderModeResponse: SetReaderModeResponseInterface = {
    devices: devices.map(d => {
      return {
        __typename: 'SetDeviceResponse',
        deviceName: d.deviceName,
        status: null}
      } ),
    error: null,
    requestUUID: null,
    status: null
  };

  return new Promise(async (resolve, reject) => {
    const input = {
      deviceSource: deviceSource,
      devices: devices,
      emails: emails,
      method: 'put',
      mode: mode,
	    path: '/v1/DeviceMode',
      processWithDelaySeconds: processWithDelaySeconds,
      requestedBy: requestedBy,
      siteName: siteName,
      siteRegion: siteRegion
    };
    try {
      createUserAction(
        {
          actionName: UserActionNames.SetReaderMode,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              input: input
            })
        });
      const response = await API.graphql(graphqlOperation(setReaderModeMutation,
        {
          input: input
        })) as GraphQLResult<APIt.SetReaderModeMutation>;
      createUserAction(
        {
          actionName: UserActionNames.SetReaderMode,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              response: response,
              input: input
            })
        });
      debug(`setReaderMode() response is ${JSON.stringify(response)}`);
      setReaderModeResponse.status = response.data?.setReaderMode?.status;
      setReaderModeResponse.requestUUID = response.data?.setReaderMode?.requestUUID;
      if (response.data?.setReaderMode?.devices)
        setReaderModeResponse.devices = response.data?.setReaderMode?.devices as APIt.SetDeviceResponse[];
      debug(`setReaderMode() setReaderModeResponse is ${JSON.stringify(setReaderModeResponse)}`);
      resolve(setReaderModeResponse);
    } catch(error: any) {
      console.error('setReaderMode() error is ', error);
      createUserAction(
        {
          actionName: UserActionNames.SetReaderModeError,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              error: error,
              input: input
            })
        });
      if (error.errors[0]) {
        setReaderModeResponse.error = error.errors[0].message;
      } else {
        setReaderModeResponse.error = error;
      }
      reject(setReaderModeResponse);
    }
  });
}

export interface SetInputMaskResponseInterface {
  devices: APIt.SetDeviceResponse[];
  error: string | null | undefined;
  requestUUID: string | null | undefined;
  status: number | null | undefined;
}

export const setInputMask = async (
  deviceSource: string,
  devices: { deviceName: string, deviceHref: string | undefined }[],
  emails: string,
  masked: boolean,
  processWithDelaySeconds: number,
  requestedBy: string,
  siteName: string,
  siteRegion: string): Promise<SetInputMaskResponseInterface> =>
{
  debug(`setInputMask() deviceSource is ${deviceSource} devices is ${JSON.stringify(devices)} emails is ${emails} masked is ${masked} processWithDelaySeconds is ${processWithDelaySeconds} requestedBy is ${requestedBy} siteName is ${siteName} siteRegion is ${siteRegion}`);

  let setInputMaskResponse: SetInputMaskResponseInterface = {
    devices: devices.map(d => {
      return {
        __typename: 'SetDeviceResponse',
        deviceName: d.deviceName,
        status: null}
      } ),
    error: null,
    requestUUID: null,
    status: null
  };

  return new Promise(async (resolve, reject) => {
    const input = {
      deviceSource: deviceSource,
      devices: devices,
      emails: emails || '',
      masked: masked,
      method: 'post',
	    path: '/v1/InputMask',
      processWithDelaySeconds: processWithDelaySeconds,
      requestedBy: requestedBy,
      siteName: siteName,
      siteRegion: siteRegion
    };
    try {
      createUserAction(
        {
          actionName: UserActionNames.SetInputMask,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              input: input
            })
        });
      debug(`setInputMask() input is ${JSON.stringify(input)}`);
      const response = await API.graphql(graphqlOperation(setInputMaskMutation,
        {
          input: input
        })) as GraphQLResult<APIt.SetInputMaskMutation>;
      createUserAction(
        {
          actionName: UserActionNames.SetInputMask,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              response: response,
              input: input
            })
        });
      debug(`setInputMask() response is ${JSON.stringify(response)}`);
      setInputMaskResponse.status = response.data?.setInputMask?.status;
      setInputMaskResponse.requestUUID = response.data?.setInputMask?.requestUUID;
      if (response.data?.setInputMask?.devices)
        setInputMaskResponse.devices = response.data?.setInputMask?.devices as APIt.SetDeviceResponse[];
      debug(`setInputMask() setInputMaskResponse is ${JSON.stringify(setInputMaskResponse)}`);
      resolve(setInputMaskResponse);
    } catch(error: any) {
      console.error('setInputMask() error is ', error);
      createUserAction(
        {
          actionName: UserActionNames.SetInputMaskError,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              error: error,
              input: input
            })
        });
      if (error.errors[0]) {
        setInputMaskResponse.error = error.errors[0].message;
      } else {
        setInputMaskResponse.error = error;
      }
      reject(setInputMaskResponse);
    }
  });
}

export interface SetReaderDoorHeldMaskResponseInterface {
  devices: APIt.SetDeviceResponse[],
  error: string | null | undefined;
  requestUUID: string | null | undefined;
  status: number | null | undefined;
}

export const setReaderDoorHeldMask = async (
  deviceSource: string,
  devices: { deviceName: string, deviceHref: string | undefined }[],
  emails: string,
  masked: boolean,
  processWithDelaySeconds: number,
  requestedBy: string,
  siteName: string,
  siteRegion: string): Promise<SetReaderDoorHeldMaskResponseInterface> =>
{
  debug(`setReaderDoorHeldMask() deviceSource is ${deviceSource} devices is ${JSON.stringify(devices)} emails is ${emails} masked is ${masked} processWithDelaySeconds is ${processWithDelaySeconds} requestedBy is ${requestedBy} siteName is ${siteName} siteRegion is ${siteRegion}`);

  let setReaderDoorHeldMaskResponse: SetReaderDoorHeldMaskResponseInterface = {
    devices: devices.map(d => {
      return {
        __typename: 'SetDeviceResponse',
        deviceName: d.deviceName,
        status: null}
      } ),
    error: null,
    requestUUID: null,
    status: null
  };

  return new Promise(async (resolve, reject) => {
    const input = {
      deviceSource: deviceSource,
      devices: devices,
      emails: emails,
      masked: masked,
      method: 'post',
	    path: '/v1/ReaderDoorHeldMask',
      processWithDelaySeconds: processWithDelaySeconds,
      requestedBy: requestedBy,
      siteName: siteName,
      siteRegion: siteRegion
    };
    try {
      createUserAction(
        {
          actionName: UserActionNames.SetReaderDoorHeldMask,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              input: input
            })
        });
      debug(`setReaderDoorHeldMask() input is ${JSON.stringify(input)}`);
      const response = await API.graphql(graphqlOperation(setReaderDoorHeldMaskMutation,
        {
          input: input
        })) as GraphQLResult<APIt.SetReaderDoorHeldMaskMutation>;
      createUserAction(
        {
          actionName: UserActionNames.SetReaderDoorHeldMask,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              response: response,
              input: input
            })
        });
      debug(`setReaderDoorHeldMask() response is ${JSON.stringify(response)}`);
      setReaderDoorHeldMaskResponse.status = response.data?.setReaderDoorHeldMask?.status;
      setReaderDoorHeldMaskResponse.requestUUID = response.data?.setReaderDoorHeldMask?.requestUUID;
      if (response.data?.setReaderDoorHeldMask?.devices)
        setReaderDoorHeldMaskResponse.devices = response.data?.setReaderDoorHeldMask?.devices as APIt.SetDeviceResponse[];
      debug(`setReaderDoorHeldMask() setReaderDoorHeldMaskResponse is ${JSON.stringify(setReaderDoorHeldMaskResponse)}`);
      resolve(setReaderDoorHeldMaskResponse);
    } catch(error: any) {
      console.error('setReaderDoorHeldMask() error is ', error);
      createUserAction(
        {
          actionName: UserActionNames.SetReaderDoorHeldMaskError,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              error: error,
              input: input
            })
        });
      if (error.errors[0]) {
        setReaderDoorHeldMaskResponse.error = error.errors[0].message;
      } else {
        setReaderDoorHeldMaskResponse.error = error;
      }
      reject(setReaderDoorHeldMaskResponse);
    }
  });
}

export interface SetReaderDoorForcedMaskResponseInterface {
  devices: APIt.SetDeviceResponse[],
  error: string | null | undefined;
  requestUUID: string | null | undefined;
  status: number | null | undefined;
}

export const setReaderDoorForcedMask = async (
  deviceSource: string,
  devices: { deviceName: string, deviceHref: string | undefined }[],
  emails: string,
  masked: boolean,
  processWithDelaySeconds: number,
  requestedBy: string,
  siteName: string,
  siteRegion: string): Promise<SetReaderDoorForcedMaskResponseInterface> =>
{
  debug(`setReaderDoorForcedMask() deviceSource is ${deviceSource} devices is ${JSON.stringify(devices)} emails is ${emails} masked is ${masked} processWithDelaySeconds is ${processWithDelaySeconds} requestedBy is ${requestedBy} siteName is ${siteName} siteRegion is ${siteRegion}`);

  let setReaderDoorForcedMaskResponse: SetReaderDoorForcedMaskResponseInterface = {
    devices: devices.map(d => {
      return {
        __typename: 'SetDeviceResponse',
        deviceName: d.deviceName,
        status: null}
      } ),
    error: null,
    requestUUID: null,
    status: null
  };

  return new Promise(async (resolve, reject) => {
    const input = {
      deviceSource: deviceSource,
      devices: devices,
      emails: emails,
      masked: masked,
      method: 'post',
      path: '/v1/ReaderDoorForcedMask',
      processWithDelaySeconds: processWithDelaySeconds,
      requestedBy: requestedBy,
      siteName: siteName,
      siteRegion: siteRegion
    };
    try {
      createUserAction(
        {
          actionName: UserActionNames.SetReaderDoorForcedMask,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              input: input
            })
        });
      debug(`setReaderDoorForcedMask() input is ${JSON.stringify(input)}`);
      const response = await API.graphql(graphqlOperation(setReaderDoorForcedMaskMutation,
        {
          input: input
        })) as GraphQLResult<APIt.SetReaderDoorForcedMaskMutation>;
      createUserAction(
        {
          actionName: UserActionNames.SetReaderDoorForcedMask,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              response: response,
              input: input
            })
        });
      debug(`setReaderDoorForcedMask() response is ${JSON.stringify(response)}`);
      setReaderDoorForcedMaskResponse.status = response.data?.setReaderDoorForcedMask?.status;
      setReaderDoorForcedMaskResponse.requestUUID = response.data?.setReaderDoorForcedMask?.requestUUID;
      if (response.data?.setReaderDoorForcedMask?.devices)
        setReaderDoorForcedMaskResponse.devices = response.data?.setReaderDoorForcedMask?.devices as APIt.SetDeviceResponse[];
      debug(`setReaderDoorForcedMask() setReaderDoorForcedMaskResponse is ${JSON.stringify(setReaderDoorForcedMaskResponse)}`);
      resolve(setReaderDoorForcedMaskResponse);
    } catch(error: any) {
      console.error('setReaderDoorForcedMask() error is ', error);
      createUserAction(
        {
          actionName: UserActionNames.SetReaderDoorForcedMaskError,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              error: error,
            input: input
            })
        });
      if (error.errors[0]) {
        setReaderDoorForcedMaskResponse.error = error.errors[0].message;
      } else {
        setReaderDoorForcedMaskResponse.error = error;
      }
      reject(setReaderDoorForcedMaskResponse);
    }
  });
}

export interface SetOnguardReaderMaskResponseInterface {
  devices: APIt.SetDeviceResponse[],
  error: string | null | undefined;
  requestUUID: string | null | undefined;
  status: number | null | undefined;
}

export async function setOnguardReaderMasking
  (region: string,
  devices: APIt.ChildDevice[],
  actionTypeId: number,
  masked: boolean,
  requestedBy: string,
  delay: number): Promise<SetOnguardReaderMaskResponseInterface>
{
  debug(`setOnguardReaderMasking() region is ${region} devices is ${JSON.stringify(devices)} actionTypeId is ${actionTypeId} masked is ${masked} requestedBy is ${requestedBy} delay is ${delay}`);

  let setOnguardReaderMaskResponse: SetOnguardReaderMaskResponseInterface = { devices: [], error: null, requestUUID: null, status: 202 };

  let stage = 'prod';

  for (let device of devices) {
    if (!device.DeviceName) continue;
    let path = `/FlexQuery/SilencioV2_Insert_DHO_DFO_Task/region/${region}/stage/${stage === 'test' ? 'BETA' : stage.toUpperCase()}/`;
    path += `?ACTION_TYPEID=${actionTypeId}&SEGMENTID=${device.SegmentID}&PANELID=${device.Parent_DeviceID}&DEVICEID=${device.Child_DeviceID}&EXECUTION_VALUE=${masked ? 1 : 0}&RUNAFTER_SECONDS=${delay}&outputformat=json`
    let actionName: UserActionNames;
    if (actionTypeId === OnguardReaderMaskActionTypes.doorForcedOpen)
      actionName = UserActionNames.SetReaderDoorForcedMask;
    else
      actionName = UserActionNames.SetReaderDoorHeldMask;
    debug(`setOnguardReaderMasking() path is ${path}`);
    try {
      createUserAction(
        {
          actionName: actionName,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              input: path
            })
        });
      const response = await API.graphql(graphqlOperation(setPACSDataAPIReaderMaskMutation,
        {
          path
        })) as GraphQLResult<APIt.SetPacsDataApiReaderMaskMutation>;
      createUserAction(
        {
          actionName: actionName,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              response: response,
              input: path
            })
        });
      debug(`setOnguardReaderMasking() response is ${JSON.stringify(response)}`);
      setOnguardReaderMaskResponse.devices.push(
        {
          __typename: "SetDeviceResponse",
          deviceName: device.DeviceName,
          status: (response.data?.setPACSDataAPIReaderMask?.status === 200 ? 'Success' : 'Failure')
        }
      );
    } catch(error) {
      console.error('setOnguardReaderMasking() error is ', error);
      createUserAction(
        {
          actionName: actionName,
          username: requestedBy,
          parameters: JSON.stringify(
            {
              error: error,
            input: path
            })
        });
    }
  }

  return new Promise(async (resolve, reject) => {
    debug(`setOnguardReaderMasking() setOnguardReaderMaskResponse is ${JSON.stringify(setOnguardReaderMaskResponse)}`);
    return resolve(setOnguardReaderMaskResponse);
  });
}